"use client";

import { useEffect, useMemo, type ReactNode } from "react";

import { Transition } from "@headlessui/react";

import { dictionaries } from "@/dictionaries/content";
import { cn } from "@/shared/helpers/cn.helper";

import {
  type IBoundaryPayload,
  type ISchoolPayload,
  type ISelectionPayload,
  type ISchoolClusterPayload,
  type IProjectClusterPayload,
  type IProjectPayload,
  type IStudentClusterPayload,
  type IStudentPayload,
  type IZonesPayload,
  type ISearchMarkerPayload,
  SelectedDataType,
  useMapPanelContext,
} from "../map-panel-context";

import { DetailsPanelBackButton } from "./components/details-panel-back-button";
import { DetailsPanelContainer } from "./components/details-panel-container";
import { PROJECT_PANEL_BACK_DICTIONARY } from "./components/project-cluster-summary.constants";
import { SCHOOL_PANEL_BACK_DICTIONARY } from "./components/school-cluster-summary.constants";
import { StudentListReportActions } from "./components/student-list-report-actions";
import { getDetailsPanelTitle } from "./get-details-panel-title";
import { renderDetailsPanelContent } from "./render-details-panel-content";
import { resolveStudentListReportActionModel } from "./resolve-student-list-report-action-model";

import type { BoundarySchoolSelectVariant } from "./components/boundary-panel-content";

export interface DetailsPanelSources {
  student?: {
    getData: () => IStudentPayload | null;
    isLoading?: boolean;
  };
  school?: {
    getData: () => ISchoolPayload | null;
    isLoading?: boolean;
  };
  selection?: {
    getData: () => ISelectionPayload | null;
    isLoading?: boolean;
  };
  boundary?: {
    getData: () => IBoundaryPayload | null;
    isLoading?: boolean;
  };
  studentCluster?: {
    getData: () => IStudentClusterPayload | null;
    isLoading?: boolean;
  };
  schoolCluster?: {
    getData: () => ISchoolClusterPayload | null;
    isLoading?: boolean;
    onSchoolSelect?: (schoolId: string) => void;
  };
  project?: {
    getData: () => IProjectPayload | null;
    isLoading?: boolean;
  };
  projectCluster?: {
    getData: () => IProjectClusterPayload | null;
    isLoading?: boolean;
    onProjectSelect?: (projectId: string) => void;
  };
  zones?: {
    getData: () => IZonesPayload | null;
    isLoading?: boolean;
    onZoneSelect?: (previewId: string, documentId: string) => void;
    /** Skeleton/content Summary cards. Map-level zones set false. Default true. */
    showSummary?: boolean;
  };
  searchMarker?: {
    getData: () => ISearchMarkerPayload | null;
  };
}

interface MapPanelDetailsPanelProps {
  widthClassName?: string;
  className?: string;
  show?: boolean;
  type?: SelectedDataType;
  onClose?: () => void;
  sources?: DetailsPanelSources;
  onBackToSchoolList?: () => void;
  showBackToSchoolList?: boolean;
  onBackToProjectList?: () => void;
  showBackToProjectList?: boolean;
  onBackToZonesList?: () => void;
  showBackToZonesList?: boolean;
  footer?: ReactNode;
  boundarySchoolSelectVariant?: BoundarySchoolSelectVariant;
}

export const MapPanelDetailsPanel: React.FC<MapPanelDetailsPanelProps> = ({
  show,
  type,
  onClose,
  sources,
  onBackToSchoolList: onBackToSchoolListProp,
  showBackToSchoolList,
  onBackToProjectList: onBackToProjectListProp,
  showBackToProjectList,
  onBackToZonesList: onBackToZonesListProp,
  showBackToZonesList,
  footer,
  boundarySchoolSelectVariant,
}) => {
  const { tabs, activeKey, selectedData, setSelectedData } =
    useMapPanelContext();

  const activeTab = tabs.find((t) => t.key === activeKey);

  const getStudentData = sources?.student?.getData;
  const getSchoolData = sources?.school?.getData;
  const getSelectionData = sources?.selection?.getData;
  const getBoundaryData = sources?.boundary?.getData;
  const getStudentClusterData = sources?.studentCluster?.getData;
  const getSchoolClusterData = sources?.schoolCluster?.getData;
  const getProjectData = sources?.project?.getData;
  const getProjectClusterData = sources?.projectCluster?.getData;
  const onSchoolClusterSelect = sources?.schoolCluster?.onSchoolSelect;
  const onProjectClusterSelect = sources?.projectCluster?.onProjectSelect;
  const getZonesData = sources?.zones?.getData;
  const onZoneSelect = sources?.zones?.onZoneSelect;
  const getSearchMarkerData = sources?.searchMarker?.getData;
  const isStudentLoading = sources?.student?.isLoading ?? false;
  const isSchoolLoading = sources?.school?.isLoading ?? false;
  const isSelectionLoading = sources?.selection?.isLoading ?? false;
  const isBoundaryLoading = sources?.boundary?.isLoading ?? false;
  const isStudentClusterLoading = sources?.studentCluster?.isLoading ?? false;
  const isSchoolClusterLoading = sources?.schoolCluster?.isLoading ?? false;
  const isProjectLoading = sources?.project?.isLoading ?? false;
  const isProjectClusterLoading = sources?.projectCluster?.isLoading ?? false;
  const isZonesLoading = sources?.zones?.isLoading ?? false;
  const zonesShowSummary = sources?.zones?.showSummary ?? true;

  const studentData = useMemo(() => getStudentData?.() ?? null, [getStudentData]);
  const schoolData = useMemo(() => getSchoolData?.() ?? null, [getSchoolData]);
  const selectionData = useMemo(() => getSelectionData?.() ?? null, [getSelectionData]);
  const boundaryData = useMemo(() => getBoundaryData?.() ?? null, [getBoundaryData]);
  const studentClusterData = useMemo(
    () => getStudentClusterData?.() ?? null,
    [getStudentClusterData],
  );
  const schoolClusterData = useMemo(
    () => getSchoolClusterData?.() ?? null,
    [getSchoolClusterData],
  );
  const projectData = useMemo(() => getProjectData?.() ?? null, [getProjectData]);
  const projectClusterData = useMemo(
    () => getProjectClusterData?.() ?? null,
    [getProjectClusterData],
  );
  const zonesData = useMemo(() => getZonesData?.() ?? null, [getZonesData]);
  const searchMarkerData = useMemo(
    () => getSearchMarkerData?.() ?? null,
    [getSearchMarkerData],
  );

  useEffect(() => {
    if (!type) return;

    switch (type) {
      case SelectedDataType.STUDENT: {
        setSelectedData({
          type: SelectedDataType.STUDENT,
          payload: studentData,
        });
        break;
      }
      case SelectedDataType.SCHOOL: {
        setSelectedData({
          type: SelectedDataType.SCHOOL,
          payload: schoolData,
        });
        break;
      }
      case SelectedDataType.SELECTION: {
        setSelectedData({
          type: SelectedDataType.SELECTION,
          payload: selectionData,
        });
        break;
      }
      case SelectedDataType.BOUNDARY: {
        setSelectedData({
          type: SelectedDataType.BOUNDARY,
          payload: boundaryData,
        });
        break;
      }
      case SelectedDataType.STUDENT_CLUSTER: {
        setSelectedData({
          type: SelectedDataType.STUDENT_CLUSTER,
          payload: studentClusterData,
        });
        break;
      }
      case SelectedDataType.SCHOOL_CLUSTER: {
        setSelectedData({
          type: SelectedDataType.SCHOOL_CLUSTER,
          payload: schoolClusterData,
        });
        break;
      }
      case SelectedDataType.PROJECT: {
        setSelectedData({
          type: SelectedDataType.PROJECT,
          payload: projectData,
        });
        break;
      }
      case SelectedDataType.PROJECT_CLUSTER: {
        setSelectedData({
          type: SelectedDataType.PROJECT_CLUSTER,
          payload: projectClusterData,
        });
        break;
      }
      case SelectedDataType.ZONES: {
        setSelectedData({
          type: SelectedDataType.ZONES,
          payload: zonesData,
        });
        break;
      }
      case SelectedDataType.SEARCH_MARKER: {
        setSelectedData({
          type: SelectedDataType.SEARCH_MARKER,
          payload: searchMarkerData,
        });
        break;
      }
    }
  }, [
    setSelectedData,
    type,
    studentData,
    schoolData,
    selectionData,
    boundaryData,
    studentClusterData,
    schoolClusterData,
    projectData,
    projectClusterData,
    zonesData,
    searchMarkerData,
  ]);

  const panelContent = useMemo(
    () =>
      renderDetailsPanelContent({
        selectedData,
        selectionData,
        isStudentLoading,
        isSchoolLoading,
        isSelectionLoading,
        isBoundaryLoading,
        isStudentClusterLoading,
        isSchoolClusterLoading,
        isProjectLoading,
        isProjectClusterLoading,
        onSchoolClusterSelect,
        onProjectClusterSelect,
        onZoneSelect,
        isZonesLoading,
        zonesShowSummary,
        boundarySchoolSelectVariant,
      }),
    [
      selectedData,
      selectionData,
      onSchoolClusterSelect,
      onProjectClusterSelect,
      onZoneSelect,
      isStudentLoading,
      isSchoolLoading,
      isSelectionLoading,
      isBoundaryLoading,
      isStudentClusterLoading,
      isSchoolClusterLoading,
      isProjectLoading,
      isProjectClusterLoading,
      isZonesLoading,
      zonesShowSummary,
      boundarySchoolSelectVariant,
    ],
  );

  const panelTitle = useMemo(() => getDetailsPanelTitle(type), [type]);
  const studentListReportActionModel = useMemo(
    () => resolveStudentListReportActionModel(selectedData?.type === type ? selectedData : null),
    [selectedData, type],
  );
  const isSearchMarkerPanel = type === SelectedDataType.SEARCH_MARKER;
  const isFitHeightPanel = isSearchMarkerPanel;

  const defaultBackFooter = useMemo(() => {
    if (
      type === SelectedDataType.SCHOOL &&
      showBackToSchoolList &&
      onBackToSchoolListProp
    ) {
      return (
        <DetailsPanelBackButton
          label={SCHOOL_PANEL_BACK_DICTIONARY.backToSchoolList}
          onClick={onBackToSchoolListProp}
        />
      );
    }

    if (
      type === SelectedDataType.PROJECT &&
      showBackToProjectList &&
      onBackToProjectListProp
    ) {
      return (
        <DetailsPanelBackButton
          label={PROJECT_PANEL_BACK_DICTIONARY.backToProjectList}
          onClick={onBackToProjectListProp}
        />
      );
    }

    if (
      (type === SelectedDataType.BOUNDARY || type === SelectedDataType.SELECTION) &&
      showBackToZonesList &&
      onBackToZonesListProp
    ) {
      return (
        <DetailsPanelBackButton
          label={dictionaries.mapPage.zonesPanel.backToZonesList}
          onClick={onBackToZonesListProp}
        />
      );
    }

    return null;
  }, [
    type,
    showBackToSchoolList,
    onBackToSchoolListProp,
    showBackToProjectList,
    onBackToProjectListProp,
    showBackToZonesList,
    onBackToZonesListProp,
  ]);

  const resolvedFooter = useMemo(() => {
    const navigationFooter = footer ?? defaultBackFooter;
    if (!navigationFooter && !studentListReportActionModel) {
      return null;
    }

    return (
      <>
        {navigationFooter}
        {studentListReportActionModel ? (
          <StudentListReportActions
            getPreviewRequest={studentListReportActionModel.getPreviewRequest}
            selectionShapes={studentListReportActionModel.selectionShapes}
          />
        ) : null}
      </>
    );
  }, [defaultBackFooter, footer, studentListReportActionModel]);

  if (!activeTab) return null;

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Transition
      enter="transition-all duration-200 ease-out"
      enterFrom="translate-x-4 opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transition-all duration-200 ease-in"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-4 opacity-0"
      show={show ?? false}
    >
      <div
        className={cn(
          "pointer-events-auto absolute right-4 top-4 z-10 flex",
          isFitHeightPanel
            ? "h-fit items-start"
            : "bottom-4 h-[calc(100%-2rem)]",
        )}
      >
        <DetailsPanelContainer
          contentHeight={isFitHeightPanel ? "fit" : "full"}
          footer={resolvedFooter}
          title={panelTitle}
          onClose={handleClose}
        >
          {panelContent}
        </DetailsPanelContainer>
      </div>
    </Transition>
  );
};
